import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbAlertCircle, TbChevronRight, TbClock, TbVideo } from "solid-icons/tb";
import { createMemo, createSignal, Match, Show, Switch } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { type MediaStorageLocation, useChatRuntime } from "../runtime/ChatRuntime";
import { ProcessingSpinner } from "../ui";
import { sanitizeForFilename } from "../util/download";
import { MediaDownloadButton } from "./MediaDownloadButton";
import { type ArtifactStatus, useArtifactStatus } from "./useArtifactStatus";
import { useSignedImageUrl } from "./useSignedImageUrl";

interface VideoMetadata {
  prompt?: string;
  aspectRatio?: string;
  durationSeconds?: number;
}

// A render starts with a pending block; the final block follows only when the
// render finishes inside the turn. Otherwise the pending block is all the chat
// ever holds, and the card reads the outcome from the artifact's own status.
interface VideoData {
  type: "video";
  status?: "rendering";
  artifactId: string;
  storageLocation: MediaStorageLocation;
  metadata?: VideoMetadata;
}

const parseVideoText = (text: string | undefined): VideoData | null => {
  const parsed = safeParseBlockJson(text) as VideoData | null;
  if (parsed?.type !== "video" || !parsed.storageLocation) return null;
  return parsed.status !== "rendering" || parsed.artifactId ? parsed : null;
};

const parseVideo = (block: StreamingBlock): VideoData | null =>
  parseVideoText(
    block.finalContent?.type === "text" && block.finalContent.text
      ? block.finalContent.text
      : getBlockContent(block.blockId)
  );

export const hasFinalVideo = (artifactId: string, texts: Array<string | undefined>): boolean =>
  texts.some((text) => {
    if (!text?.includes(artifactId)) return false;
    const video = parseVideoText(text);
    return video !== null && video.status !== "rendering" && video.artifactId === artifactId;
  });

type VideoRenderState =
  // `url` is set when the status lookup already signed the file.
  | { kind: "ready"; location: MediaStorageLocation; url?: string }
  | { kind: "rendering" }
  | { kind: "failed" }
  | { kind: "stalled" };

export const videoRenderState = (
  video: VideoData,
  live: ArtifactStatus | undefined,
  capped: boolean
): VideoRenderState => {
  const location = video.storageLocation;
  if (video.status !== "rendering") return { kind: "ready", location };
  if (live?.status === "ready") return { kind: "ready", location, url: live.url };
  if (live?.status === "failed") return { kind: "failed" };
  // An artifact the server can't place may still exist elsewhere, so it reads
  // as unavailable for now rather than as a failure.
  return capped || live?.status === "missing" ? { kind: "stalled" } : { kind: "rendering" };
};

const useVideoRender = (block: () => StreamingBlock) => {
  const video = createMemo(() => parseVideo(block()));
  // A re-parse of the same block must not release and re-wait the card, which
  // would drop its signed URL.
  const pending = createMemo(
    () => {
      const v = video();
      return v?.status === "rendering" ? v : undefined;
    },
    undefined,
    { equals: (a, b) => a?.artifactId === b?.artifactId }
  );
  const artifact = useArtifactStatus(pending);
  const state = () => {
    const v = video();
    return v ? videoRenderState(v, artifact.status(), artifact.capped()) : undefined;
  };
  // Every poll rebuilds the state objects; an equal location must not re-sign
  // the URL and reload the player.
  const location = createMemo(
    () => {
      const s = state();
      return s?.kind === "ready" ? s.location : undefined;
    },
    undefined,
    { equals: (a, b) => a?.bucket === b?.bucket && a?.key === b?.key }
  );
  const statusUrl = createMemo(() => {
    const s = state();
    return s?.kind === "ready" ? s.url : undefined;
  });
  return { video, state, location, statusUrl };
};

// "16:9" → "16 / 9", so the player's box is sized before the file loads.
const cssAspectRatio = (ratio: string | undefined): string => {
  const [w, h] = (ratio ?? "").split(":").map(Number);
  return w && h ? `${w} / ${h}` : "16 / 9";
};

const useVideoLabels = (video: () => VideoData | null) => {
  const rt = useChatRuntime();
  const title = createMemo(() => {
    const prompt = video()?.metadata?.prompt;
    if (!prompt) return rt.t("activity.video_generation");
    return prompt.length > 60 ? `${prompt.slice(0, 60)}...` : prompt;
  });
  const subtitle = createMemo(() => {
    const meta = video()?.metadata;
    return [rt.t("entity.video"), meta?.aspectRatio, meta?.durationSeconds ? `${meta.durationSeconds}s` : undefined]
      .filter(Boolean)
      .join(" · ");
  });
  return { title, subtitle };
};

/**
 * Hosts with a content viewer (bluesky) get a compact card that opens it; hosts
 * that render media inline (chief) play the video in the thread. The image URL
 * resolver signs the mp4 with its stored content type.
 */
export function VideoGenerationRenderer(props: { block: StreamingBlock }) {
  const rt = useChatRuntime();
  const video = createMemo(() => parseVideo(props.block));
  const superseded = createMemo(() => {
    const v = video();
    if (v?.status !== "rendering") return false;
    const texts: Array<string | undefined> = [];
    for (const operationId of rt.store.message(props.block.messageId)?.operations ?? []) {
      for (const blockId of rt.store.operation(operationId)?.blocks ?? []) {
        const content = rt.store.block(blockId)?.finalContent;
        if (content?.type === "text") texts.push(content.text);
      }
    }
    return hasFinalVideo(v.artifactId, texts);
  });

  const showInline = () =>
    Boolean(rt.actions.resolveImageUrls) && (rt.caps.inlineMedia || !rt.actions.openContentBlock);

  return (
    <Show when={!superseded()}>
      <div data-renderer="video-generation-renderer">
        <Show
          when={video()}
          fallback={
            <Show when={!props.block.isComplete}>
              <div class="flex items-center gap-3 py-2 text-sm text-secondary-text">
                <ProcessingSpinner class="size-4" />
                <span>{rt.t("generating_video")}</span>
              </div>
            </Show>
          }
        >
          <Show when={!showInline()} fallback={<VideoGenerationFullView block={props.block} />}>
            <VideoGenerationCard block={props.block} />
          </Show>
        </Show>
      </div>
    </Show>
  );
}

function VideoGenerationCard(props: { block: StreamingBlock }) {
  const rt = useChatRuntime();
  const { video, state } = useVideoRender(() => props.block);
  const { title, subtitle } = useVideoLabels(video);
  const canOpen = () => Boolean(rt.actions.openContentBlock);

  return (
    <button
      type="button"
      onClick={() => rt.actions.openContentBlock?.(props.block.blockId)}
      disabled={!canOpen()}
      class="flex w-full items-center gap-3 rounded-lg border border-primary-border/50 p-3 text-left transition-colors enabled:cursor-pointer hover:enabled:bg-canvas-hover"
    >
      <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-inset">
        <Switch fallback={<TbVideo class="size-5 text-secondary-text" />}>
          <Match when={state()?.kind === "rendering"}>
            <ProcessingSpinner class="size-5" />
          </Match>
          <Match when={state()?.kind === "failed"}>
            <TbAlertCircle class="size-5 text-secondary-text" />
          </Match>
        </Switch>
      </div>
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm font-medium text-primary-text">{title()}</div>
        <div class="mt-0.5 text-xs text-muted-text">
          <Switch fallback={subtitle()}>
            <Match when={state()?.kind === "rendering"}>{rt.t("generating_video")}</Match>
            <Match when={state()?.kind === "failed"}>{rt.t("video_generation.failed")}</Match>
            <Match when={state()?.kind === "stalled"}>{rt.t("video_generation.still_rendering")}</Match>
          </Switch>
        </div>
      </div>
      <Show when={canOpen()}>
        <TbChevronRight class="size-4 shrink-0 text-muted-text" />
      </Show>
    </button>
  );
}

export function VideoGenerationFullView(props: { block: StreamingBlock }) {
  const rt = useChatRuntime();
  const { video, state, location, statusUrl } = useVideoRender(() => props.block);
  const { title, subtitle } = useVideoLabels(video);
  const signed = useSignedImageUrl(() => (statusUrl() ? undefined : location()));
  const [playbackFailed, setPlaybackFailed] = createSignal(false);
  const filename = () => `${sanitizeForFilename(title()).slice(0, 80) || "video"}.mp4`;

  return (
    <div class="overflow-hidden rounded-lg border border-primary-border/50">
      <div
        class="relative max-h-96 w-full bg-inset"
        style={{ "aspect-ratio": cssAspectRatio(video()?.metadata?.aspectRatio) }}
      >
        <Switch>
          <Match when={state()?.kind === "rendering"}>
            <div class="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 text-sm text-secondary-text">
              <ProcessingSpinner class="size-6" />
              <span>{rt.t("generating_video")}</span>
            </div>
          </Match>
          <Match when={state()?.kind === "stalled"}>
            <div class="absolute inset-0 flex items-center justify-center gap-2 p-3 text-center text-sm text-secondary-text">
              <TbClock class="size-4 shrink-0" />
              <span>{rt.t("video_generation.still_rendering")}</span>
            </div>
          </Match>
          <Match when={state()?.kind === "failed"}>
            <div class="absolute inset-0 flex items-center justify-center gap-2 p-3 text-center text-sm text-secondary-text">
              <TbAlertCircle class="size-4 shrink-0" />
              <span>{rt.t("video_generation.failed")}</span>
            </div>
          </Match>
          <Match when={signed.error() || playbackFailed()}>
            <div class="absolute inset-0 flex items-center justify-center gap-2 p-3 text-sm text-secondary-text">
              <TbVideo class="size-4 shrink-0" />
              <span>{rt.t("error_failed_to_load_video")}</span>
            </div>
          </Match>
          <Match when={statusUrl() ?? signed.url()}>
            {(url) => (
              <video
                src={url()}
                controls
                playsinline
                preload="metadata"
                class="absolute inset-0 size-full object-contain"
                onError={() => setPlaybackFailed(true)}
              />
            )}
          </Match>
          <Match when={signed.loading()}>
            <div class="absolute inset-0 flex items-center justify-center">
              <ProcessingSpinner class="size-6" />
            </div>
          </Match>
        </Switch>
      </div>
      <div class="flex items-start justify-between gap-2 px-3 py-2">
        <div class="min-w-0">
          <div class="truncate text-sm font-medium text-primary-text">{title()}</div>
          <div class="mt-0.5 text-xs text-muted-text">{subtitle()}</div>
        </div>
        <Show when={rt.actions.downloadMedia && location()}>
          {(loc) => <MediaDownloadButton location={loc()} filename={filename()} />}
        </Show>
      </div>
    </div>
  );
}
